import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { OrganizationMembership, User, useUserStore } from '@/store/user.ts'
import { UserRoles } from '@/lib/constants/constants.ts'

import { PopoverProperty } from '@/components/common/propertyPopoverOverlay/Property.tsx'
import i18n from '@/i18n.ts'
import { DateRange } from 'react-day-picker'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const convertFormData = (formData: FormData) => {
  const dataObject: Record<string, unknown> = {}
  formData.forEach((value, key) => {
    dataObject[key] = value
  })

  return dataObject
}

export const getCurrentUser = (): User | null => {
  const userStore = useUserStore.getState()

  return userStore.user
}

export const getCurrentOrganization = (): OrganizationMembership | null => {
  const userStore = useUserStore.getState()

  return userStore.currentOrganizationMembership
}

export const getCurrentRole = (): UserRoles | null => {
  const userStore = useUserStore.getState()
  return userStore.currentOrganizationMembership?.role?.code || null
}

export const isSuperuser = (): boolean => {
  const userStore = useUserStore.getState()
  return (userStore.user as { isSpecialUser?: boolean } | null)?.isSpecialUser || false
}

export const flatten = (property: PopoverProperty) => {
  const propertiesFlat = [property]
  if (property.children) {
    property.children.forEach(child => {
      propertiesFlat.push(...flatten(child))
    })
  } else {
    propertiesFlat.push(property)
  }
  return Array.from(new Set(propertiesFlat))
}

type FormatDateOptionsT = {
  excludeTime?: boolean
  excludeYear?: boolean
  forceTimeFormat?: boolean
  monthFormat?: '2-digit' | 'short'
}

/**
 * Formats date and time to a human-readable format.
 * Example: "Dzisiaj o 12:34", "Wczoraj o 12:34", "01.01.2022 12:34"
 * @param dateTimeString - date and time string in ISO format
 * @param options - options for formatting
 */
export function formatDateTime(
  dateTimeString: string | null | undefined | Date,
  options?: FormatDateOptionsT,
): string {
  if (!dateTimeString) return ''
  const inputDate = new Date(dateTimeString)
  const now = new Date()
  const monthFormat = options?.monthFormat || '2-digit'

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
  }
  const formatDate = (date: Date): string => {
    if (options?.excludeYear) {
      return date.toLocaleDateString('pl-PL', { day: '2-digit', month: monthFormat })
    }
    return date.toLocaleDateString('pl-PL', { day: '2-digit', month: monthFormat, year: 'numeric' })
  }

  const isSameDay = (date1: Date, date2: Date): boolean =>
    date1.toDateString() === date2.toDateString()

  if (options?.excludeTime) return `${formatDate(inputDate)}`
  if (options?.forceTimeFormat) return `${formatDate(inputDate)} ${formatTime(inputDate)}`

  const isYesterday = (date: Date): boolean => {
    const yesterday = new Date(now)
    yesterday.setDate(now.getDate() - 1)
    return isSameDay(date, yesterday)
  }

  if (isSameDay(inputDate, now)) {
    return `${i18n.t('common.todayAt')} ${formatTime(inputDate)}`
  }

  if (isYesterday(inputDate)) {
    return `${i18n.t('common.yesterdayAt')} ${formatTime(inputDate)}`
  }

  return `${formatDate(inputDate)} ${formatTime(inputDate)}`
}

/**
 * Check if `date` is invalid
 * @param dateString - date and time string in ISO format
 */
export default function safeParseDate(
  dateString: string | null | undefined | number,
): Date | undefined {
  if (!dateString) return undefined
  const date = new Date(dateString)
  // Check if `date` is invalid
  if (isNaN(date.getTime())) {
    return undefined
  }
  return date
}

export function getRangeFromRangeString(rangeString?: string) {
  //
  if (!rangeString) return
  const rangeArr = rangeString.split(`,`)
  const rangeFrom = rangeArr[0]
  const rangeTo = rangeArr[1]

  const fromDate = safeParseDate(rangeFrom)
  const toDate = safeParseDate(rangeTo)

  if (!fromDate || !toDate) {
    // console.log(`🚧 not valid date range skipping `)
    return
  }

  return { from: fromDate, to: toDate }
}

/**
 * Safely parse a JSON string, returning a fallback value on failure.
 */
export function safeJsonParse<T>(value: string, fallback: T): T {
  try {
    return JSON.parse(value) as T
  } catch {
    return fallback
  }
}

export function createDateRange(
  offsetDays: number,
  direction: 'past' | 'future' = 'past',
): DateRange {
  const today = new Date()
  const resultDate = new Date(today)

  if (direction === 'past') {
    resultDate.setDate(today.getDate() - Math.abs(offsetDays))
  } else {
    resultDate.setDate(today.getDate() + Math.abs(offsetDays))
  }

  return {
    from: direction === 'past' ? resultDate : today,
    to: direction === 'past' ? today : resultDate,
  }
}
